import { SetStateAction, Dispatch, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, Download } from "lucide-react";

/**
 * Interface for the parsed download URLs JSON structure
 */
interface DownloadUrlsData {
  urls: string[];
}

/**
 * DownloadUrlDialog component displays a dialog with download URLs from a sheet agent run.
 * The downloadUrls prop is a JSON string that contains an object with a "urls" array.
 *
 * @component
 * @param downloadUrls - JSON string containing the download URLs (format: {"urls": ["url1", "url2", ...]})
 * @param triggerLabel - Optional label for the trigger button (default: "Download Files")
 * @param iconDisplay - Controls what to display on the trigger button: "text" (text only), "icon" (icon only), or "both" (default)
 * @param textOnlyTrigger - If true, renders the trigger as text only without a button (default: false)
 * @example
 * <DownloadUrlDialog
 *   downloadUrls='{"urls": ["https://example.com/file1.xlsx", "https://example.com/file2.xlsx"]}'
 *   triggerLabel="View Downloads"
 *   iconDisplay="both"
 * />
 * @example
 * <DownloadUrlDialog
 *   downloadUrls='{"urls": ["https://example.com/file1.xlsx"]}'
 *   triggerLabel="Click here"
 *   textOnlyTrigger={true}
 * />
 */
interface DownloadUrlDialogProps {
  /** JSON string containing download URLs */
  downloadUrls: string | null | undefined;
  /** Optional label for the trigger button */
  triggerLabel?: string;
  /** Optional variant for the trigger button */
  triggerVariant?: "default" | "outline" | "ghost" | "secondary" | "destructive" | "link";
  /** Optional size for the trigger button */
  triggerSize?: "default" | "sm" | "lg" | "icon";
  /** Controls what to display on the trigger button: "text" (text only), "icon" (icon only), or "both" (default) */
  iconDisplay?: "text" | "icon" | "both";
  /** If true, renders the trigger as text only without a button */
  textOnlyTrigger?: boolean;
  /** Controlled open state */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: Dispatch<SetStateAction<boolean>>;
}

function DownloadUrlDialog({
  downloadUrls,
  triggerLabel,
  triggerVariant = "outline",
  triggerSize = "default",
  iconDisplay = "both",
  textOnlyTrigger = false,
  open,
  onOpenChange,
}: DownloadUrlDialogProps) {
  const { t } = useTranslation("modals");
  const defaultTriggerLabel = t("sheetagent.downloadurls.defaulttriggerlabel");

  // Parse the downloadUrls JSON string
  const urls = useMemo(() => {
    // FIXME: Why is backend setting to string "null"?
    if (!downloadUrls || downloadUrls === "null") {
      return [];
    }

    try {
      const parsed: DownloadUrlsData = JSON.parse(downloadUrls);
      return parsed.urls || [];
    } catch (error) {
      console.error("Failed to parse downloadUrls:", error);
      return [];
    }
  }, [downloadUrls]);

  // If no URLs, don't render the component
  if (urls.length === 0) {
    return null;
  }

  // Determine button size based on iconDisplay
  const buttonSize = iconDisplay === "icon" ? "icon" : triggerSize;
  const displayTriggerLabel = triggerLabel || defaultTriggerLabel;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        {textOnlyTrigger ? (
          <span
            className="cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
            }}>
            {displayTriggerLabel}
          </span>
        ) : (
          <Button
            variant={triggerVariant}
            size={buttonSize}
            onClick={(e) => {
              e.stopPropagation();
            }}>
            {iconDisplay === "icon" || iconDisplay === "both" ? (
              <Download className="h-4 w-4" />
            ) : null}
            {iconDisplay === "text" || iconDisplay === "both" ? (
              <span className={iconDisplay === "both" ? "ml-2" : ""}>{displayTriggerLabel}</span>
            ) : null}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("sheetagent.downloadurls.title")}</DialogTitle>
          <DialogDescription>
            {t("sheetagent.downloadurls.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {urls.map((url, index) => {
            // Extract filename from URL for display
            const fileName = url.split("/").pop() || `${t("sheetagent.downloadurls.filefallback")} ${index + 1}`;
            return (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 rounded-md border hover:bg-accent hover:text-accent-foreground transition-colors group">
                <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
                <span className="flex-1 truncate text-sm">{fileName}</span>
                <span className="text-xs text-muted-foreground shrink-0">{t("sheetagent.downloadurls.openlink")}</span>
              </a>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DownloadUrlDialog;
